import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { AnalyticsQueryDashboard, SPARK_DEMAND_DIMENSIONS, SparkDemandMetricsByTime } from '@pubstack/common/src/analytics/query'
import { Alert } from '@pubstack/ui/components/Alert'
import { useAtomValue } from 'jotai'
import { DateTime } from 'luxon'
import { FunctionComponent } from 'react'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { Forbidden } from '~/components/error/Forbidden'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { useIsSparkDemandDashboardEnabled } from '~/hooks/useSparkDemand'
import { AnalyticsPage } from '~/modules/analytics/AnalyticsPage'
import { Context, contextState, TimeRange } from '~/state'
import { SparkDemandTimeline } from './SparkDemandTimeline'

const DIMENSIONS: Dimension[] = [...SPARK_DEMAND_DIMENSIONS]
// The filter values come from the server-side auction table, the same as on the Server-side dashboard.
const DASHBOARD: AnalyticsQueryDashboard = { name: 'server-side', filterType: 'auctions' }

export const SparkDemandPage: FunctionComponent = () => {
  const sparkDemandDashboardEnabled = useIsSparkDemandDashboardEnabled()
  const context = useAtomValue(contextState)
  const currencySymbol = useScopeCurrency()
  const breadcrumbs = useBreadcrumbs()
  const { tz } = context.timeRange
  const yesterday = DateTime.now().setZone(tz).startOf('day').minus({ days: 1 })
  const timeRange: TimeRange = { from: yesterday.startOf('month'), to: yesterday.endOf('day'), tz }
  const buildContext = (context: Context): Context => ({ ...context, timeRange })

  const analyticsQueryByTime = useAnalyticsQueryTanstack<SparkDemandMetricsByTime>('spark-demand.metrics.by.time', DIMENSIONS, buildContext(context), {}, true, sparkDemandDashboardEnabled)

  if (!sparkDemandDashboardEnabled) {
    return <Forbidden />
  }

  return (
    <AnalyticsPage
      onRefreshClick={() => analyticsQueryByTime.refetch()}
      filterDimensions={DIMENSIONS}
      dashboard={DASHBOARD}
      title={breadcrumbs}
      buildContext={buildContext}
      disableDatepicker
      overrideSelectedTimeRange={timeRange}
      timePresets={[]}
    >
      <div className="flex flex-col gap-4">
        <Alert variant={'tertiary'}>Revenue and impressions are estimated for {yesterday.toFormat('LLLL')}, up to yesterday. The final amounts are consolidated after the month closes.</Alert>
        <SparkDemandTimeline
          isLoading={analyticsQueryByTime.isFetching}
          error={analyticsQueryByTime.isError}
          empty={!analyticsQueryByTime.isFetching && !analyticsQueryByTime.data?.epoch.length}
          onRefreshClick={() => analyticsQueryByTime.refetch()}
          data={analyticsQueryByTime.data ?? { epoch: [] }}
          currencySymbol={currencySymbol}
          currentEpoch={DateTime.now()}
          timezone={tz}
        />
      </div>
    </AnalyticsPage>
  )
}
